import { z } from 'zod';

// ---------------------------------------------------------------------------
// Sensitive field sanitisation (C-29)
// ---------------------------------------------------------------------------

const SENSITIVE_KEYS = new Set(['client_secret', 'api_key', 'authorization', 'password']);

function isSensitiveKey(key: string): boolean {
  const lower = key.toLowerCase();
  return SENSITIVE_KEYS.has(lower) || lower.endsWith('_token');
}

/**
 * Recursively sanitise a parsed JSON object, replacing sensitive field values
 * with `[REDACTED]`. Handles nested objects and arrays. (C-29)
 */
export function sanitiseJson(value: unknown): unknown {
  if (value === null || value === undefined) return value;
  if (Array.isArray(value)) return value.map(sanitiseJson);
  if (typeof value === 'object') {
    const result: Record<string, unknown> = {};
    for (const [k, v] of Object.entries(value as Record<string, unknown>)) {
      result[k] = isSensitiveKey(k) ? '[REDACTED]' : sanitiseJson(v);
    }
    return result;
  }
  return value;
}

/**
 * Parse a JSON string, sanitise it, and return the sanitised string.
 * Returns the original string if parsing fails.
 */
export function sanitiseJsonString(raw: string | null | undefined): string | null {
  if (raw === null || raw === undefined) return null;
  try {
    const parsed: unknown = typeof raw === 'object' ? raw : JSON.parse(raw);
    const sanitised = sanitiseJson(parsed);
    return JSON.stringify(sanitised, null, 2);
  } catch {
    return raw;
  }
}

/** Threshold in characters above which JSON bodies are truncated in the detail modal. */
export const JSON_TRUNCATION_THRESHOLD = 5120; // ~5KB

// ---------------------------------------------------------------------------
// Convera API Log DTO — mirrors backend UC-AF-03 response
// ---------------------------------------------------------------------------

export const ConveraApiLogDtoSchema = z.object({
  convera_api_log_id: z.string(),
  direction: z.string(),
  resource_type: z.string().nullish(),
  resource_id: z.string().nullish(),
  request_url: z.string(),
  request_method: z.string(),
  response_status: z.number().nullable(),
  convera_ref: z.string().nullish(),
  duration_ms: z.number().nullable(),
  error_message: z.string().nullish(),
  created_at: z.string(),
});

export type ConveraApiLogDto = z.infer<typeof ConveraApiLogDtoSchema>;

// ---------------------------------------------------------------------------
// Detail DTO — list-row fields plus sanitised request/response bodies. Backed
// by GET /api/convera/logs/:id. Bodies arrive already redacted by the
// backend sanitiser; the frontend treats them as opaque JSON for display.
// ---------------------------------------------------------------------------

export const ConveraApiLogDetailDtoSchema = ConveraApiLogDtoSchema.extend({
  request_body: z.unknown().nullish(),
  response_body: z.unknown().nullish(),
});

export type ConveraApiLogDetailDto = z.infer<typeof ConveraApiLogDetailDtoSchema>;

// ---------------------------------------------------------------------------
// Paginated response — matches PaginatedResponse<T> { data, meta } from the
// CBS API (see ConveraPassthroughController.getLogs).
// ---------------------------------------------------------------------------

export const ConveraLogsResponseSchema = z.object({
  data: z.array(ConveraApiLogDtoSchema),
  meta: z.object({
    page: z.number(),
    limit: z.number(),
    total: z.number(),
    total_pages: z.number(),
    outbound_count: z.number(),
    inbound_count: z.number(),
    error_count: z.number(),
  }),
});

export type ConveraLogsResponse = z.infer<typeof ConveraLogsResponseSchema>;

// ---------------------------------------------------------------------------
// Filter & sort state
// ---------------------------------------------------------------------------

export type DirectionFilter = 'all' | 'outbound' | 'inbound';

export const DIRECTION_OPTIONS: { value: DirectionFilter; label: string }[] = [
  { value: 'all', label: 'All directions' },
  { value: 'outbound', label: 'Outbound' },
  { value: 'inbound', label: 'Inbound' },
];

// ---------------------------------------------------------------------------
// Status code colour mapping
// ---------------------------------------------------------------------------

export function getStatusVariant(
  status: number | null,
): 'success' | 'info' | 'warning' | 'error' | 'default' {
  if (status === null) return 'default';
  if (status >= 200 && status < 300) return 'success';
  if (status >= 300 && status < 400) return 'info';
  if (status >= 400 && status < 500) return 'warning';
  if (status >= 500) return 'error';
  return 'default';
}

// ---------------------------------------------------------------------------
// Direction display
// ---------------------------------------------------------------------------

export function getDirectionDisplay(direction: string): {
  icon: string;
  label: string;
  variant: 'info' | 'default';
} {
  if (direction === 'outbound') {
    return { icon: '\u2192', label: 'OUT', variant: 'info' };
  }
  return { icon: '\u2190', label: 'IN', variant: 'default' };
}
